// CategoryManagement.js - Admin Panel for Call Categorization
// Phase 4 Implementation

import React, { useState, useEffect, useCallback } from 'react';
import { 
  ChevronRight, 
  ChevronDown, 
  Plus, 
  Edit2, 
  Trash2, 
  Upload, 
  Download,
  History,
  X,
  Save
} from 'lucide-react';

const CategoryManagement = ({ apiService, user }) => {
  const [categories, setCategories] = useState([]);
  const [callTypes, setCallTypes] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [modalLevel, setModalLevel] = useState(1);
  const [formData, setFormData] = useState({ name: '', parent_id: null, level: 1 });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [xmlContent, setXmlContent] = useState('');
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadCategories = useCallback(async () => {
    try {
      const data = await apiService.getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Failed to load categories:', error);
      alert('Помилка завантаження категорій');
    }
  }, [apiService]);

  const loadCallTypes = useCallback(async () => {
    try {
      const data = await apiService.getCallTypes();
      setCallTypes(data);
    } catch (error) {
      console.error('Failed to load call types:', error);
    }
  }, [apiService]);

  useEffect(() => {
    loadCategories();
    loadCallTypes();
  }, [loadCategories, loadCallTypes]);

  const loadAuditLog = async () => {
    try {
      const data = await apiService.getAuditLog({ limit: 100 });
      setAuditLogs(data.logs);
    } catch (error) {
      console.error('Failed to load audit log:', error);
    }
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const handleCreate = (level, parentId = null) => {
    setModalMode('create');
    setModalLevel(level);
    setFormData({ name: '', parent_id: parentId, level });
    setShowModal(true);
  };

  const handleEdit = (category) => {
    setModalMode('edit');
    setSelectedCategory(category);
    setFormData({ name: category.name, parent_id: category.parent_id, level: category.level });
    setShowModal(true);
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Видалити категорію "${category.name}"?`)) return;

    try {
      await apiService.deleteCategory(category.id);
      alert('Категорію видалено');
      loadCategories();
    } catch (error) {
      console.error('Failed to delete category:', error);
      alert('Помилка видалення категорії. Можливо, вона має дочірні елементи.');
    }
  };

  const handleSaveCategory = async () => {
    if (!formData.name.trim()) {
      alert('Введіть назву категорії');
      return;
    }

    try {
      if (modalMode === 'create') {
        await apiService.createCategory(formData);
        alert('Категорію створено');
      } else {
        await apiService.updateCategory(selectedCategory.id, { name: formData.name });
        alert('Категорію оновлено');
      }
      setShowModal(false);
      loadCategories();
    } catch (error) {
      console.error('Failed to save category:', error);
      alert('Помилка збереження категорії');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await apiService.exportCategories();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `categories_${new Date().toISOString().split('T')[0]}.xml`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      alert('Експорт завершено');
    } catch (error) {
      console.error('Failed to export:', error);
      alert('Помилка експорту');
    }
  };

  const handleImportPreview = async () => {
    if (!xmlContent.trim()) {
      alert('Вставте XML вміст');
      return;
    }

    try {
      setLoading(true);
      const preview = await apiService.previewImport(xmlContent);
      setImportPreview(preview);
    } catch (error) {
      console.error('Failed to preview import:', error);
      alert('Помилка попереднього перегляду');
    } finally {
      setLoading(false);
    }
  };

  const handleImportExecute = async () => {
    if (!window.confirm('Підтвердити імпорт?')) return;

    try {
      setLoading(true);
      await apiService.importCategories(xmlContent);
      alert('Імпорт завершено');
      setShowImportModal(false);
      setXmlContent('');
      setImportPreview(null);
      loadCategories();
    } catch (error) {
      console.error('Failed to import:', error);
      alert('Помилка імпорту');
    } finally {
      setLoading(false);
    }
  };

  const renderCategoryTree = (nodes, level = 1) => {
    return nodes.map(node => (
      <div key={node.id} className="ml-4">
        <div className="flex items-center gap-2 py-2 hover:bg-gray-50 rounded px-2">
          <button
            onClick={() => toggleNode(node.id)}
            className="text-gray-500 hover:text-gray-700"
          >
            {node.children && node.children.length > 0 ? (
              expandedNodes.has(node.id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
            ) : (
              <span className="w-4 h-4 inline-block" />
            )}
          </button>
          
          <span className="flex-1 text-gray-900">{node.name}</span>
          <span className="text-xs text-gray-500">ID: {node.id}</span>
          
          <div className="flex gap-1">
            {level < 3 && (
              <button
                onClick={() => handleCreate(level + 1, node.id)}
                className="p-1 text-green-600 hover:bg-green-50 rounded"
                title="Додати дочірній елемент"
              >
                <Plus className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => handleEdit(node)}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded"
              title="Редагувати"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDelete(node)}
              className="p-1 text-red-600 hover:bg-red-50 rounded"
              title="Видалити"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {expandedNodes.has(node.id) && node.children && node.children.length > 0 && (
          <div className="ml-4 border-l-2 border-gray-200">
            {renderCategoryTree(node.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Управління довідниками</h1>
        <p className="text-gray-600">Керування категоріями звернень та типами дзвінків</p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <button
          onClick={() => handleCreate(1)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4" />
          Створити категорію рівня 1
        </button>
        <button
          onClick={() => setShowImportModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
        >
          <Upload className="w-4 h-4" />
          Імпорт XML
        </button>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
        >
          <Download className="w-4 h-4" />
          Експорт XML
        </button>
        <button
          onClick={() => {
            setShowAuditLog(true);
            loadAuditLog();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
        >
          <History className="w-4 h-4" />
          Журнал змін
        </button>
      </div>

      {/* Category Tree */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Дерево категорій</h2>
        <div className="border rounded-lg p-4">
          {categories.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Немає категорій. Створіть першу категорію.</p>
          ) : (
            renderCategoryTree(categories)
          )}
        </div>
      </div>

      {/* Call Types Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Типи звернень (Довідник Б)</h2>
        <div className="space-y-2">
          {callTypes.map(type => (
            <div key={type.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
              <span className="text-gray-900">{type.name}</span>
              <span className="text-xs text-gray-500">ID: {type.id}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {modalMode === 'create' ? `Створення категорії (рівень ${modalLevel})` : 'Редагування категорії'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Назва</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Введіть назву категорії"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSaveCategory}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Зберегти
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg transition-colors"
                >
                  Скасувати
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Імпорт категорій з XML</h2>
              <button onClick={() => setShowImportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">XML вміст</label>
                <textarea
                  value={xmlContent}
                  onChange={(e) => setXmlContent(e.target.value)}
                  className="w-full h-64 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="Вставте XML вміст тут..."
                />
              </div>

              <button
                onClick={handleImportPreview}
                disabled={loading}
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-400"
              >
                {loading ? 'Завантаження...' : 'Попередній перегляд'}
              </button>

              {importPreview && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h3 className="font-semibold text-gray-900 mb-3">Попередній перегляд змін:</h3>
                  
                  {importPreview.new.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-green-700 mb-1">Нові елементи ({importPreview.new.length}):</p>
                      <ul className="text-sm text-gray-700 space-y-1">
                        {importPreview.new.slice(0, 10).map((item, idx) => (
                          <li key={idx}>+ {item.name} (рівень {item.level})</li>
                        ))}
                        {importPreview.new.length > 10 && <li>... та ще {importPreview.new.length - 10}</li>}
                      </ul>
                    </div>
                  )}

                  {importPreview.updated.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-yellow-700 mb-1">Змінені елементи ({importPreview.updated.length}):</p>
                      <ul className="text-sm text-gray-700 space-y-1">
                        {importPreview.updated.slice(0, 10).map((item, idx) => (
                          <li key={idx}>~ "{item.old}" → "{item.new}"</li>
                        ))}
                        {importPreview.updated.length > 10 && <li>... та ще {importPreview.updated.length - 10}</li>}
                      </ul>
                    </div>
                  )}

                  {importPreview.deleted.length > 0 && (
                    <div className="mb-3">
                      <p className="text-sm font-medium text-red-700 mb-1">Видалені елементи ({importPreview.deleted.length}):</p>
                      <ul className="text-sm text-gray-700 space-y-1">
                        {importPreview.deleted.slice(0, 10).map((item, idx) => (
                          <li key={idx}>- {item.name} (рівень {item.level})</li>
                        ))}
                        {importPreview.deleted.length > 10 && <li>... та ще {importPreview.deleted.length - 10}</li>}
                      </ul>
                    </div>
                  )}

                  <button
                    onClick={handleImportExecute}
                    disabled={loading}
                    className="w-full mt-4 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:bg-gray-400"
                  >
                    {loading ? 'Імпорт...' : 'Підтвердити імпорт'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Audit Log Modal */}
      {showAuditLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Журнал змін</h2>
              <button onClick={() => setShowAuditLog(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left">Дата/Час</th>
                    <th className="px-4 py-2 text-left">Користувач</th>
                    <th className="px-4 py-2 text-left">Дія</th>
                    <th className="px-4 py-2 text-left">Деталі</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map(log => (
                    <tr key={log.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-2">{new Date(log.changed_at).toLocaleString('uk-UA')}</td>
                      <td className="px-4 py-2">{log.changed_by}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          log.action === 'CREATE' ? 'bg-green-100 text-green-800' :
                          log.action === 'UPDATE' ? 'bg-yellow-100 text-yellow-800' :
                          log.action === 'DELETE' ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-600">{log.details}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryManagement;
